import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Calendar, CalendarPlus, Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import type { SeasonResponse, SeasonWritable } from '@bm/contracts';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Alert, Button, Card, Field, Input } from '../components/ui';
import { Modal } from '../components/Modal';
import { OrgTabs } from '../components/OrgTabs';

const STATUS_LABELS: Record<SeasonResponse['status'], string> = {
  planning: 'Geplant',
  active: 'Aktiv',
  archived: 'Archiviert',
};
const STATUS_STYLES: Record<SeasonResponse['status'], string> = {
  planning: 'bg-blue-100 text-blue-700',
  active: 'bg-green-100 text-green-700',
  archived: 'bg-slate-100 text-slate-500',
};

const formatDate = (iso: string | null) =>
  iso ? new Date(`${iso}T00:00:00`).toLocaleDateString('de-DE') : null;

type FormState = { name: string; startsOn: string; endsOn: string; status: SeasonResponse['status'] };

const toFormState = (s: SeasonResponse | null): FormState => ({
  name: s?.name ?? '',
  startsOn: s?.startsOn ?? '',
  endsOn: s?.endsOn ?? '',
  status: s?.status ?? 'planning',
});

/** Anlegen/Bearbeiten teilen sich dasselbe Formular — `editing` unterscheidet
 * nur, welche Mutation beim Speichern ausgelöst wird (POST vs. PATCH). */
function SeasonFormModal({
  editing,
  onClose,
}: {
  editing: SeasonResponse | null | 'new';
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const season = editing === 'new' ? null : editing;
  const [form, setForm] = useState<FormState>(() => toFormState(season));
  const [error, setError] = useState<string | null>(null);

  const asBody = (): SeasonWritable => ({
    name: form.name.trim(),
    startsOn: form.startsOn || null,
    endsOn: form.endsOn || null,
    status: form.status,
  });

  const save = useMutation({
    mutationFn: () =>
      season
        ? api(`/season/seasons/${season.id}`, { method: 'PATCH', orgScoped: true, body: asBody() })
        : api('/season/seasons', { method: 'POST', orgScoped: true, body: asBody() }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['seasons'] });
      onClose();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Speichern fehlgeschlagen.'),
  });

  return (
    <Modal
      title={season ? 'Saison bearbeiten' : 'Neue Saison'}
      icon={<Calendar size={18} />}
      onClose={onClose}
    >
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (form.name.trim()) save.mutate();
        }}
      >
        {error && <Alert>{error}</Alert>}
        <Field label="Name">
          <Input
            autoFocus
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="2026 / 2027"
            required
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Beginn">
            <Input
              type="date"
              value={form.startsOn}
              onChange={(e) => setForm((f) => ({ ...f, startsOn: e.target.value }))}
            />
          </Field>
          <Field label="Ende">
            <Input
              type="date"
              value={form.endsOn}
              onChange={(e) => setForm((f) => ({ ...f, endsOn: e.target.value }))}
            />
          </Field>
        </div>
        <Field label="Status">
          <select
            className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand-accent focus:ring-1 focus:ring-brand-accent"
            value={form.status}
            onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as SeasonResponse['status'] }))}
          >
            {(Object.keys(STATUS_LABELS) as SeasonResponse['status'][]).map((s) => (
              <option key={s} value={s}>
                {STATUS_LABELS[s]}
              </option>
            ))}
          </select>
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button type="submit" disabled={!form.name.trim() || save.isPending}>
            {save.isPending ? 'Speichert …' : 'Speichern'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export function OrgSeasonsPage() {
  const qc = useQueryClient();
  const { can } = useAuth();
  const [editing, setEditing] = useState<SeasonResponse | null | 'new'>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const canEdit = can('season.edit');

  const { data: seasons } = useQuery({
    queryKey: ['seasons'],
    queryFn: () => api<SeasonResponse[]>('/season/seasons', { orgScoped: true }),
  });

  const deleteSeason = useMutation({
    mutationFn: (id: string) => api(`/season/seasons/${id}`, { method: 'DELETE', orgScoped: true }),
    onSuccess: () => {
      setDeleteError(null);
      void qc.invalidateQueries({ queryKey: ['seasons'] });
    },
    onError: (e) => setDeleteError(e instanceof ApiError ? e.message : 'Löschen fehlgeschlagen.'),
  });

  return (
    <div className="space-y-4">
      <h1 className="hidden text-xl font-semibold md:block">Verein</h1>
      <OrgTabs />

      <Card title="Saisons">
        <p className="mb-3 text-xs text-slate-400">
          Saisons bündeln Mannschaftszuordnungen und Saisonspiele. Die Saisonspiel-Seite selbst
          zeigt nur noch eine Auswahl bestehender Saisons — anlegen, bearbeiten und löschen passiert hier.
        </p>
        {deleteError && <Alert>{deleteError}</Alert>}

        {seasons && seasons.length > 0 ? (
          <ul className="mb-4 divide-y divide-slate-100">
            {seasons.map((s) => (
              <li key={s.id} className="flex flex-wrap items-center justify-between gap-2 py-3">
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-accent/10 text-brand-accent">
                    <Calendar size={16} />
                  </div>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="truncate text-sm font-semibold text-slate-800">{s.name}</span>
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[s.status]}`}>
                        {STATUS_LABELS[s.status]}
                      </span>
                    </div>
                    <p className="truncate text-xs text-slate-500">
                      {formatDate(s.startsOn) && formatDate(s.endsOn)
                        ? `${formatDate(s.startsOn)} – ${formatDate(s.endsOn)} · `
                        : ''}
                      {s.teams.length} Mannschaft{s.teams.length === 1 ? '' : 'en'} · {s.matchCount} Spiel
                      {s.matchCount === 1 ? '' : 'e'}
                    </p>
                  </div>
                </div>
                {canEdit && (
                  <div className="flex shrink-0 gap-1">
                    <button
                      type="button"
                      title="Bearbeiten"
                      aria-label="Bearbeiten"
                      onClick={() => setEditing(s)}
                      className="flex h-8 w-8 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100"
                    >
                      <Pencil size={15} />
                    </button>
                    <button
                      type="button"
                      title="Löschen"
                      aria-label="Löschen"
                      disabled={deleteSeason.isPending}
                      onClick={() => {
                        const extra =
                          s.teams.length > 0
                            ? ` ${s.teams.length} Mannschaftszuordnung(en) werden entfernt.`
                            : '';
                        const matchNote =
                          s.matchCount > 0
                            ? ` ${s.matchCount} Saisonspiel(e) bleiben erhalten, aber ohne Saison-Zuordnung.`
                            : '';
                        if (confirm(`Saison „${s.name}" wirklich löschen?${extra}${matchNote}`)) {
                          deleteSeason.mutate(s.id);
                        }
                      }}
                      className="flex h-8 w-8 items-center justify-center rounded-full text-red-600 hover:bg-red-50 disabled:opacity-50"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="mb-4 text-sm text-slate-500">Noch keine Saisons angelegt.</p>
        )}

        {canEdit && (
          <Button variant="ghost" className="gap-1.5" onClick={() => setEditing('new')}>
            <CalendarPlus size={16} />
            Neue Saison
          </Button>
        )}
      </Card>

      {editing && <SeasonFormModal editing={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}
